// src/pages/PillarIndustrialAI.js
// Hungarian topic pillar page for "ipari mesterséges intelligencia" / "ipari AI".
// Hungarian-only: it is registered as a HU_ONLY route, so it is prerendered and
// listed in the sitemap under /hu only, and carries no hreflang alternates until
// an English counterpart exists.
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/SEO/PageMeta';
import { TREE_LANG, PILLAR_SLUGS, prefixFor, INDEXABLE_LANGS } from '../langPath';

const BASE = 'https://robomindsystems.ai';

const PillarIndustrialAI = () => {
    const { t } = useTranslation();
    const k = 'pillar_ai';
    // This page has a different slug in every language, so the alternate cluster
    // has to be built explicitly rather than by prefixing one shared path.
    const PATH = PILLAR_SLUGS[TREE_LANG];
    const alternates = INDEXABLE_LANGS.map((lang) => ({
        lang,
        href: `${BASE}${prefixFor(lang)}${PILLAR_SLUGS[lang]}`,
    }));
    const arr = (key) => {
        const v = t(`${k}.${key}`, { returnObjects: true, defaultValue: [] });
        return Array.isArray(v) ? v : [];
    };

    const faq = arr('faq');
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        inLanguage: TREE_LANG,
        mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
    };
    const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        inLanguage: TREE_LANG,
        headline: t(`${k}.h1`),
        description: t(`${k}.meta_description`),
        mainEntityOfPage: `${BASE}${prefixFor(TREE_LANG)}${PATH}`,
        author: { '@type': 'Organization', name: 'ROBOMIND Systems' },
        publisher: {
            '@type': 'Organization',
            name: 'ROBOMIND Systems',
            logo: { '@type': 'ImageObject', url: `${BASE}/robomind-logo.png` },
        },
    };
    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        inLanguage: TREE_LANG,
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'ROBOMIND Systems', item: `${BASE}${prefixFor(TREE_LANG) || '/'}` },
            { '@type': 'ListItem', position: 2, name: t(`${k}.h1`), item: `${BASE}${prefixFor(TREE_LANG)}${PATH}` },
        ],
    };

    return (
        <>
            <PageMeta
                title={t(`${k}.meta_title`)}
                description={t(`${k}.meta_description`)}
                keywords={t(`${k}.keywords`)}
                path={PATH}
                alternates={alternates}
            />
            {[articleSchema, faqSchema, breadcrumbSchema].map((s, i) => (
                <script key={i} type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }} />
            ))}

            {/* Header */}
            <div className="bg-slate-900 py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-aos="fade-up">
                    <h1 className="text-4xl font-bold text-white mb-6">{t(`${k}.h1`)}</h1>
                    <p className="text-lg text-gray-300 leading-relaxed">{t(`${k}.lead`)}</p>
                </div>
            </div>

            <article className="bg-white py-14">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s1_title`)}</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">{t(`${k}.s1_p1`)}</p>
                        <p className="text-gray-700 leading-relaxed">{t(`${k}.s1_p2`)}</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s2_title`)}</h2>
                        <p className="text-gray-700 leading-relaxed mb-6">{t(`${k}.s2_intro`)}</p>
                        <div className="space-y-4">
                            {arr('s2_items').map((it, i) => (
                                <div key={i} className="border-l-4 border-accent pl-4">
                                    <h3 className="font-bold text-gray-900">{it.t}</h3>
                                    <p className="text-gray-700 leading-relaxed">{it.d}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s3_title`)}</h2>
                        {['s3_p1', 's3_p2', 's3_p3'].map((p) => (
                            <p key={p} className="text-gray-700 leading-relaxed mb-4">{t(`${k}.${p}`)}</p>
                        ))}
                        <p className="text-gray-800 leading-relaxed bg-gray-50 border border-gray-200 rounded-lg p-4">
                            {t(`${k}.s3_p4`)}
                        </p>
                        <p className="mt-4">
                            <Link to="/solutions/data-collection" className="text-primary font-semibold hover:underline">
                                {t('solutions.data_collection.title')} →
                            </Link>
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s4_title`)}</h2>
                        <p className="text-gray-700 leading-relaxed mb-6">{t(`${k}.s4_intro`)}</p>
                        <div className="space-y-8">
                            {arr('s4_cases').map((c, i) => (
                                <div key={i}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{c.t}</h3>
                                    <p className="text-gray-700 leading-relaxed mb-2">{c.d}</p>
                                    <Link to={c.link} className="text-primary font-semibold hover:underline">
                                        {c.link_label} →
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s5_title`)}</h2>
                        <p className="text-gray-700 leading-relaxed mb-6">{t(`${k}.s5_p1`)}</p>
                        <div className="grid md:grid-cols-3 gap-6 mb-6">
                            {arr('s5_items').map((it, i) => (
                                <div key={i} className="bg-gray-50 rounded-lg p-5">
                                    <h3 className="font-bold text-gray-900 mb-2">{it.t}</h3>
                                    <p className="text-gray-700 text-sm leading-relaxed">{it.d}</p>
                                </div>
                            ))}
                        </div>
                        <p className="text-gray-700 leading-relaxed">{t(`${k}.s5_p2`)}</p>
                        <p className="mt-4">
                            <Link to="/solutions/edge-ai" className="text-primary font-semibold hover:underline">
                                {t('solutions.edge_ai.title')} →
                            </Link>
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s6_title`)}</h2>
                        <ol className="space-y-4">
                            {arr('s6_steps').map((s, i) => (
                                <li key={i}>
                                    <h3 className="font-bold text-gray-900">{s.t}</h3>
                                    <p className="text-gray-700 leading-relaxed">{s.d}</p>
                                </li>
                            ))}
                        </ol>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s7_title`)}</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">{t(`${k}.s7_p1`)}</p>
                        <ul className="list-disc pl-6 space-y-2 mb-4 text-gray-700">
                            {arr('s7_items').map((it, i) => <li key={i}>{it}</li>)}
                        </ul>
                        <p className="text-gray-700 leading-relaxed">{t(`${k}.s7_p2`)}</p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-6">{t(`${k}.s8_title`)}</h2>
                        <div className="space-y-6">
                            {faq.map((item, i) => (
                                <div key={i}>
                                    <h3 className="font-bold text-gray-900 mb-1">{item.q}</h3>
                                    <p className="text-gray-700 leading-relaxed">{item.a}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">{t(`${k}.s9_title`)}</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">{t(`${k}.s9_p1`)}</p>
                        <Link to="/case-studies" className="text-primary font-semibold hover:underline">
                            {t(`${k}.s9_link_label`)} →
                        </Link>
                    </section>

                    <section className="bg-slate-900 rounded-xl p-8 text-center">
                        <h2 className="text-2xl font-bold text-white mb-3">{t(`${k}.cta_title`)}</h2>
                        <p className="text-gray-300 mb-6 max-w-2xl mx-auto">{t(`${k}.cta_p`)}</p>
                        <Link to="/contact"
                            className="inline-block bg-primary hover:bg-primary-hover text-white font-bold py-3 px-8 rounded-lg transition-colors">
                            {t(`${k}.cta_button`)}
                        </Link>
                    </section>
                </div>
            </article>
        </>
    );
};

export default PillarIndustrialAI;
